import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/api/client'

const nullableString = z.string().nullable().optional()

const billingProductSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: nullableString,
  amount: z.number(),
  currency: z.string(),
  billing_interval: z.string(),
  participant_limit: z.number().nullable().optional(),
  amount_due: z.number().nullable().optional(),
  is_upgrade: z.boolean().optional(),
  available: z.boolean().optional(),
  upgrade_from_product_key: z.string().nullable().optional(),
  tax_behavior: z.string().optional(),
  provider: z.string().optional(),
})

const paymentOrderSchema = z.object({
  id: z.number(),
  product_key: z.string(),
  status: z.string(),
  amount: z.number(),
  currency: z.string(),
  net_amount: z.number().nullable().optional(),
  tax_amount: z.number().nullable().optional(),
  gross_amount: z.number().nullable().optional(),
  paid_at: nullableString,
})

const licenseSchema = z.object({
  id: z.number(),
  product_key: z.string(),
  status: z.string(),
  participant_limit: z.number().nullable().optional(),
  starts_at: nullableString,
  ends_at: nullableString,
})

const productsResponseSchema = z.object({
  data: z.array(billingProductSchema),
})

const billingStateSchema = z.object({
  poule_id: z.number(),
  activation_status: z.enum(['pending', 'active']).default('pending'),
  license_required: z.boolean().default(true),
  license: licenseSchema.nullable(),
  latest_order: paymentOrderSchema.nullable(),
  products: z.array(billingProductSchema),
})

const checkoutResponseSchema = z.object({
  checkout_url: z.string().url(),
  order: paymentOrderSchema,
})

export type BillingProduct = z.infer<typeof billingProductSchema>

export function fetchCompanyPouleProducts() {
  return api.get('/billing/company-poule-products').then((response) => productsResponseSchema.parse(response).data)
}

export function fetchPouleBilling(pouleId: number) {
  return api.get('/poules/' + pouleId + '/billing').then((response) => billingStateSchema.parse(response))
}

export function usePouleBilling(pouleId: number | null, polling = false) {
  return useQuery({
    queryKey: ['poule-billing', pouleId],
    queryFn: () => fetchPouleBilling(pouleId as number),
    enabled: pouleId !== null,
    refetchInterval: polling ? 3000 : false,
  })
}

export function useCreateCompanyPouleCheckout(pouleId: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { productKey: string; customerType?: 'business' | 'consumer' }) => {
      const response = await api.post('/poules/' + pouleId + '/billing/checkout', {
        product_key: payload.productKey,
        customer_type: payload.customerType ?? 'business',
      })
      return checkoutResponseSchema.parse(response)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['poule-billing', pouleId] })
    },
  })
}
